import React from 'react';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * NotificationBadge - Small red badge with count for navigation items
 * Usage: <NotificationBadge entityType="Customer" />
 */
const NotificationBadge = ({ 
  entityType = null, 
  level = null, 
  className = '',
  showZero = false,
  maxCount = 99 
}) => {
  const { getEntityCount, getLevelCount, counts } = useNotifications();

  // Determine what count to show
  let count = 0;
  if (entityType) {
    count = getEntityCount(entityType);
  } else if (level) {
    count = getLevelCount(level);
  } else {
    count = counts.total;
  }

  // Don't show badge if count is 0 and showZero is false
  if (count === 0 && !showZero) {
    return null;
  }

  // Format count display (e.g., 99+ for counts over maxCount)
  const displayCount = count > maxCount ? `${maxCount}+` : count.toString();

  return (
    <span 
      className={`
        absolute -bottom-1 -right-1 
        inline-flex items-center justify-center 
        min-w-[18px] h-[18px] 
        text-xs font-bold text-white 
        bg-red-500 
        rounded-full 
        border-2 border-white
        z-10
        ${className}
      `}
      aria-label={`${count} unread notifications`}
    >
      {displayCount}
    </span>
  );
};

/**
 * InlineNotificationBadge - Larger badge for use in content areas
 */
export const InlineNotificationBadge = ({ 
  entityType = null, 
  level = null, 
  className = '',
  showZero = false,
  maxCount = 999,
  size = 'md' // 'sm', 'md', 'lg'
}) => {
  const { getEntityCount, getLevelCount, counts } = useNotifications();

  let count = 0;
  if (entityType) {
    count = getEntityCount(entityType);
  } else if (level) {
    count = getLevelCount(level);
  } else {
    count = counts.total;
  }

  if (count === 0 && !showZero) {
    return null;
  }

  const displayCount = count > maxCount ? `${maxCount}+` : count.toString();

  const sizeClasses = {
    sm: 'min-w-[20px] h-5 text-xs px-1.5',
    md: 'min-w-[24px] h-6 text-sm px-2',
    lg: 'min-w-[28px] h-7 text-base px-2.5'
  };

  return (
    <span 
      className={`
        inline-flex items-center justify-center 
        ${sizeClasses[size]}
        font-bold text-white 
        bg-red-500 
        rounded-full
        ${className}
      `}
      aria-label={`${count} unread notifications`}
    >
      {displayCount}
    </span>
  );
};

/**
 * EntityNotificationSummary - Shows breakdown of notifications by entity
 */
export const EntityNotificationSummary = ({ className = '' }) => {
  const { counts } = useNotifications();

  const entityLabels = {
    customer: 'Customers',
    lead: 'Leads', 
    opportunity: 'Opportunities',
    activity: 'Activities',
    knowledge: 'Knowledge'
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <h4 className="text-sm font-medium text-gray-700">Notifications by Type</h4>
      <div className="space-y-1">
        {Object.entries(counts.byEntity).map(([entityType, count]) => (
          count > 0 && (
            <div key={entityType} className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{entityLabels[entityType] || entityType}</span>
              <InlineNotificationBadge entityType={entityType} size="sm" />
            </div>
          )
        ))}
        {Object.keys(counts.byEntity).every(key => counts.byEntity[key] === 0) && (
          <p className="text-sm text-gray-500 italic">No unread notifications</p>
        )}
      </div>
    </div>
  );
};

export default NotificationBadge;